import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MessageSquarePlus, Send, Bug, Lightbulb, HelpCircle, Star } from 'lucide-react';
import api from '@/utils/api';

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showButton] = useState(true);

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500' },
    { value: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: 'text-yellow-500' },
    { value: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-500' },
    { value: 'praise', label: 'Praise', icon: Star, color: 'text-green-500' },
  ];

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter your feedback message');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/users/feedback/submit', {
        type: feedbackType,
        message: message.trim(),
        rating: rating > 0 ? rating : null,
        source: 'widget'
      });
      
      toast.success('Thank you for your feedback!');
      setOpen(false);
      setMessage('');
      setRating(0);
      setFeedbackType('suggestion');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {showButton && (
        <div className="fixed bottom-20 right-6 z-50 flex flex-col items-end gap-2">
          <Button
            onClick={() => setOpen(true)}
            className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid="feedback-widget-btn"
          >
            <MessageSquarePlus className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* Feedback Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="feedback-widget-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <MessageSquarePlus className="w-6 h-6 text-blue-500" />
              Send Feedback
            </DialogTitle>
            <DialogDescription>
              Help us improve SAFER-i™! Your feedback goes directly to our team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Feedback Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">What type of feedback?</Label>
              <div className="grid grid-cols-2 gap-2">
                {feedbackTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        feedbackType === type.value
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      data-testid={`feedback-type-${type.value}`}
                    >
                      <Icon className={`w-5 h-5 ${type.color}`} />
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="feedback-message" className="text-sm font-medium">
                Your feedback
              </Label>
              <Textarea
                id="feedback-message"
                placeholder={
                  feedbackType === 'bug' 
                    ? "Describe the issue you encountered..."
                    : feedbackType === 'suggestion'
                    ? "Share your idea or suggestion..."
                    : feedbackType === 'question'
                    ? "What would you like to know?"
                    : "Tell us what you love about SAFER-i™..."
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="resize-none"
                data-testid="feedback-message"
              />
            </div>

            {/* Optional Rating */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Rate your experience <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(rating === star ? 0 : star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
                    data-testid={`widget-star-${star}`}
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <button
                    onClick={() => setRating(0)}
                    className="ml-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              className="gap-2"
              data-testid="submit-widget-feedback-btn"
            >
              {submitting ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Feedback
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
